import React, { useEffect, useState } from 'react';

import { NotificationSettingsPanel } from './NotificationSettingsPage';
import { api, ApiError } from '../lib/api';
import { changeLanguage } from '../providers/I18nProvider';
import { useAuthStore } from '../stores/auth.store';

// ----- Coming Soon badge -----
function ComingSoon() {
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
      Coming soon
    </span>
  );
}

// ----- Types -----

type SettingsTab = 'profile' | 'privacy' | 'notifications' | 'data' | 'account';

type Visibility = 'Public' | 'Connections Only' | 'Private';

type NotificationType =
  | 'connection_request'
  | 'connection_accepted'
  | 'reaction'
  | 'reply'
  | 'direct_message'
  | 'security_alert';

interface NotificationSetting {
  type: NotificationType;
  label: string;
  description: string;
  inApp: boolean;
  email: boolean;
  emailDisabled?: boolean;
}

// ----- Constants -----
const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'data', label: 'Data' },
  { id: 'account', label: 'Account' },
];

const DEFAULT_NOTIFICATION_SETTINGS: NotificationSetting[] = [
  { type: 'connection_request', label: 'Connection requests', description: 'When someone sends you a connection request', inApp: true, email: true },
  { type: 'connection_accepted', label: 'Connection accepted', description: 'When someone accepts your connection request', inApp: true, email: true },
  { type: 'reaction', label: 'Reactions', description: 'When someone reacts to your post', inApp: true, email: false },
  { type: 'reply', label: 'Replies', description: 'When someone replies to your post', inApp: true, email: true },
  { type: 'direct_message', label: 'Direct messages', description: 'When you receive a new direct message', inApp: true, email: true },
  { type: 'security_alert', label: 'Security alerts', description: 'Account security notifications', inApp: true, email: true, emailDisabled: true },
];

// ----- Main Page -----

/**
 * AccountSettingsPage — tabbed layout with Profile, Privacy, Notifications, Data, Account.
 */
export function AccountSettingsPage(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  return (
    <div className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Manage your account, profile, and preferences.
          </p>
        </div>

        {/* Tab bar */}
        <div className="mb-6 flex gap-1 overflow-x-auto rounded-lg bg-surface-muted p-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex shrink-0 items-center rounded-md px-3 py-2 text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-surface-raised text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
              {(tab.id === 'notifications' || tab.id === 'data' || tab.id === 'account') && (
                <span className="ml-1.5 inline-flex items-center rounded-full bg-surface px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-text-muted">
                  Soon
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'profile' && <ProfileTab />}
        {activeTab === 'privacy' && <PrivacyTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'data' && <DataTab />}
        {activeTab === 'account' && <AccountTab />}
      </div>
    </div>
  );
}

// ----- Profile Tab -----

interface MeResponse {
  displayName?: string | null;
  role?: string | null;
  industry?: string | null;
  bio?: string | null;
  workSituation?: string | null;
  photoUrl?: string | null;
}

function ProfileTab() {
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);

  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [bio, setBio] = useState('');
  const [workSituation, setWorkSituation] = useState('');

  // Photo is shown if the user already has one; upload is temporarily disabled (S3 setup pending).
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Load current profile values from the API on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get<MeResponse>('/users/me');
        if (cancelled) return;
        setDisplayName(me.displayName ?? '');
        setRole(me.role ?? '');
        setIndustry(me.industry ?? '');
        setBio(me.bio ?? '');
        setWorkSituation(me.workSituation ?? '');
        setPhotoUrl(me.photoUrl ?? null);
      } catch {
        // Fall back to whatever the auth store already has.
        if (!cancelled && user?.display_name) setDisplayName(user.display_name);
        if (!cancelled && user?.photo_url) setPhotoUrl(user.photo_url);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.display_name, user?.photo_url]);

  function validateDisplayName(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed.length < 2) return 'Display name must be at least 2 characters.';
    if (trimmed.length > 100) return 'Display name must be 100 characters or fewer.';
    return null;
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    setSaved(false);

    const validationError = validateDisplayName(displayName);
    if (validationError) {
      setFieldError(validationError);
      return;
    }

    setSaving(true);
    try {
      // Send all editable fields. Empty optional fields are sent as null to clear them.
      const body = {
        displayName: displayName.trim(),
        role: role.trim() || null,
        industry: industry.trim() || null,
        bio: bio.trim() || null,
        workSituation: workSituation.trim() || null,
      };

      await api.patch('/users/me', body);

      // Keep the auth store's display name in sync so the rest of the UI updates.
      if (user) setUser({ ...user, display_name: body.displayName });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.status >= 500) {
        setError('Unable to save your profile. Please try again.');
      } else if (err instanceof ApiError) {
        const b = err.body as { message?: string } | null;
        setError(b?.message ?? 'Something went wrong. Please try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-surface-raised p-6">
        <p className="text-sm text-text-muted">Loading your profile…</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="rounded-lg border border-border bg-surface-raised p-6">
      <h2 className="mb-4 text-lg font-semibold text-text-primary">Profile</h2>

      {/* Photo upload area — temporarily disabled pending S3 credential setup */}
      <div className="mb-6">
        <label className="mb-2 flex items-center text-sm font-medium text-text-secondary">
          Photo
          <ComingSoon />
        </label>
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-surface-muted">
            {photoUrl ? (
              <img src={photoUrl} alt="Your profile" className="h-full w-full object-cover" />
            ) : (
              <UserIcon />
            )}
          </div>
          <button
            type="button"
            disabled
            title="Coming soon"
            className="cursor-not-allowed rounded border border-border px-3 py-1.5 text-sm font-medium text-text-muted opacity-50"
          >
            Upload photo
          </button>
          <span className="text-xs text-text-muted">JPEG or PNG, max 5 MB</span>
        </div>
      </div>

      {/* Display Name */}
      <div className="mb-4">
        <label htmlFor="settings-display-name" className="mb-1 block text-sm font-medium text-text-secondary">
          Display name
        </label>
        <input
          id="settings-display-name"
          type="text"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            if (fieldError) setFieldError(null);
          }}
          maxLength={100}
          className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
        />
        {fieldError && (
          <p className="mt-1 text-xs text-agent-red" role="alert">
            {fieldError}
          </p>
        )}
        <span className="mt-1 block text-right text-xs text-text-muted">
          {displayName.length}/100
        </span>
      </div>

      {/* Role */}
      <div className="mb-4">
        <label htmlFor="settings-role" className="mb-1 block text-sm font-medium text-text-secondary">
          Role
        </label>
        <input
          id="settings-role"
          type="text"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="e.g. Product Manager"
          className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
        />
      </div>

      {/* Industry */}
      <div className="mb-4">
        <label htmlFor="settings-industry" className="mb-1 block text-sm font-medium text-text-secondary">
          Industry
        </label>
        <input
          id="settings-industry"
          type="text"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. Technology"
          className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
        />
      </div>

      {/* Bio */}
      <div className="mb-4">
        <label htmlFor="settings-bio" className="mb-1 block text-sm font-medium text-text-secondary">
          Bio
        </label>
        <textarea
          id="settings-bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="Tell others a bit about yourself…"
          className="w-full resize-none rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
        />
        <span className="mt-1 block text-right text-xs text-text-muted">{bio.length}/500</span>
      </div>

      {/* Work Situation */}
      <div className="mb-6">
        <label htmlFor="settings-work-situation" className="mb-1 block text-sm font-medium text-text-secondary">
          Work situation
        </label>
        <textarea
          id="settings-work-situation"
          value={workSituation}
          onChange={(e) => setWorkSituation(e.target.value)}
          maxLength={2000}
          rows={4}
          placeholder="Describe your current work situation — this gives agents context when coaching you."
          className="w-full resize-none rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
        />
        <span className="mt-1 block text-right text-xs text-text-muted">
          {workSituation.length}/2000
        </span>
      </div>

      {error && (
        <p className="mb-3 text-sm text-agent-red" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="rounded bg-gail px-4 py-2 text-sm font-medium text-white hover:bg-gail-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && (
          <span className="text-sm font-medium text-gail" role="status">
            Saved ✓
          </span>
        )}
      </div>
    </form>
  );
}

// ----- Privacy Tab -----

function PrivacyTab() {
  const [visibility, setVisibility] = useState<Visibility>('Public');
  const [language, setLanguage] = useState(
    localStorage.getItem('i18nextLng') ?? 'en',
  );

  function handleLanguageChange(newLocale: string) {
    setLanguage(newLocale);
    changeLanguage(newLocale);
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    // TODO: call PATCH /users/me with visibility
  }

  return (
    <form onSubmit={handleSave} className="rounded-lg border border-border bg-surface-raised p-6">
      <h2 className="mb-4 text-lg font-semibold text-text-primary">Privacy &amp; Language</h2>

      {/* Visibility */}
      <div className="mb-4">
        <label htmlFor="settings-visibility" className="mb-1 block text-sm font-medium text-text-secondary">
          Profile visibility
          <ComingSoon />
        </label>
        <select
          id="settings-visibility"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as Visibility)}
          disabled
          className="w-full cursor-not-allowed rounded border border-border bg-surface-muted px-3 py-2 text-sm text-text-muted opacity-60"
        >
          <option value="Public">Public</option>
          <option value="Connections Only">Connections Only</option>
          <option value="Private">Private</option>
        </select>
        <p className="mt-1 text-xs text-text-muted">
          Profile visibility controls are on the way. For now, your profile follows the default privacy settings.
        </p>
      </div>

      {/* Language */}
      <div className="mb-2">
        <label htmlFor="settings-language" className="mb-1 block text-sm font-medium text-text-secondary">
          Language
        </label>
        <select
          id="settings-language"
          value={language}
          onChange={(e) => handleLanguageChange(e.target.value)}
          className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
        </select>
        <p className="mt-1 text-xs text-text-muted">Your language preference saves automatically.</p>
      </div>
    </form>
  );
}

// ----- Notifications Tab -----

function NotificationsTab() {
  const [settings, setSettings] = useState<NotificationSetting[]>(DEFAULT_NOTIFICATION_SETTINGS);

  function handleToggle(type: NotificationType, channel: 'inApp' | 'email', value: boolean) {
    setSettings((prev) =>
      prev.map((s) => (s.type === type ? { ...s, [channel]: value } : s)),
    );
    // TODO: call PATCH /notification-settings
  }

  return (
    <div>
      <h2 className="mb-4 flex items-center text-lg font-semibold text-text-primary">
        Notification Preferences
        <ComingSoon />
      </h2>
      <div className="mb-4 rounded border border-border bg-surface-muted p-3 text-sm text-text-secondary">
        Notification preferences aren&apos;t active yet. You can preview the options below, but changes
        won&apos;t be saved until this feature launches.
      </div>
      <div className="pointer-events-none opacity-60" aria-hidden="true">
        <NotificationSettingsPanel settings={settings} onToggle={handleToggle} />
      </div>
    </div>
  );
}

// ----- Data Tab -----

function DataTab() {
  return (
    <div className="rounded-lg border border-border bg-surface-raised p-6">
      <h2 className="mb-4 flex items-center text-lg font-semibold text-text-primary">
        Your Data
        <ComingSoon />
      </h2>

      <p className="mb-4 text-sm text-text-secondary">
        Export all your data including your profile, session transcripts, insights, posts, and
        connections. You&apos;ll receive a downloadable archive within a few minutes.
      </p>

      <div className="mb-4 rounded bg-surface-muted p-3">
        <p className="text-xs font-medium text-text-secondary">Export includes:</p>
        <ul className="mt-1 list-inside list-disc text-xs text-text-muted">
          <li>Profile information and settings</li>
          <li>All session transcripts and messages</li>
          <li>Saved insights and tags</li>
          <li>Community posts and replies</li>
          <li>Connection list</li>
        </ul>
      </div>

      <button
        type="button"
        disabled
        title="Coming soon"
        className="cursor-not-allowed rounded bg-byron px-4 py-2 text-sm font-medium text-white opacity-50"
      >
        Export my data
      </button>
    </div>
  );
}

// ----- Account Tab -----

function AccountTab() {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  function handleDelete() {
    if (deleteConfirmText !== 'DELETE') return;
    // TODO: call POST /account/delete
  }

  return (
    <div className="rounded-lg border border-border bg-surface-raised p-6">
      <h2 className="mb-4 flex items-center text-lg font-semibold text-text-primary">
        Delete Account
        <ComingSoon />
      </h2>

      <div className="mb-4 rounded border border-border bg-surface-muted p-3 text-sm text-text-secondary">
        Self-service account deletion isn&apos;t available yet. In the meantime, contact us and we&apos;ll
        remove your account and data for you.
      </div>

      <div className="mb-4 rounded border border-agent-red/30 bg-ray-light p-4">
        <p className="text-sm font-medium text-agent-red">Warning: This action is irreversible.</p>
        <p className="mt-1 text-xs text-text-secondary">
          Deleting your account will permanently remove all your data including sessions, insights,
          posts, and connections. This cannot be undone.
        </p>
      </div>

      {!showDeleteConfirm ? (
        <button
          disabled
          title="Coming soon"
          className="cursor-not-allowed rounded border border-agent-red px-4 py-2 text-sm font-medium text-agent-red opacity-50"
        >
          Delete my account
        </button>
      ) : (
        <div className="rounded border border-border bg-surface p-4">
          <p className="mb-3 text-sm text-text-primary">
            Type <span className="font-mono font-semibold">DELETE</span> to confirm:
          </p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            className="mb-3 w-full rounded border border-border bg-surface-raised px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-agent-red focus:outline-none focus:ring-1 focus:ring-agent-red"
            aria-label="Type DELETE to confirm"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmText('');
              }}
              className="rounded border border-border px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-muted"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteConfirmText !== 'DELETE'}
              className="rounded bg-agent-red px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Permanently delete account
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Icons -----

function UserIcon() {
  return (
    <svg className="h-8 w-8 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </svg>
  );
}
